import { useEffect, useRef, useState } from "react";
import { Streamdown } from "streamdown";
import { Render } from "../render/Render";

/**
 * URL allowlist for streamdown — the chat NEVER renders arbitrary URLs the
 * LLM might echo. Same-origin + buyer.zeemart.co + the Zeemart asset CDN.
 * streamdown v2 expects this via the `urlTransform` hook.
 */
const ALLOWED_URL_PREFIXES = [
  "https://nomni-procure.pages.dev/",
  "https://buyer.zeemart.co/",
  "https://storage.googleapis.com/zeemart-production-common-file-storage/",
  "https://images.zeemart.asia/",
];
function safeUrl(url: string): string {
  if (!url) return "";
  if (url.startsWith("/") || url.startsWith("#") || url.startsWith("mailto:")) return url;
  if (ALLOWED_URL_PREFIXES.some((p) => url.startsWith(p))) return url;
  // Anything else (raw HTTP, javascript:, data:, untrusted hosts) is neutered.
  return "#";
}

type Step = {
  id: string;
  name: string;
  label: string;
  phase: "start" | "done";
  summary?: string;
  cached?: "cross-turn" | "within-turn" | null;
  ms?: number;
};

type Msg =
  | { role: "user"; text: string }
  | { role: "assistant"; text: string; spec?: unknown; steps: Step[] };

const STARTERS = [
  "How much did I spend this week, and on which suppliers?",
  "Show me suppliers I haven't ordered from in 30 days",
  "Which POs are still pending approval?",
  "What's my top spend category and how does it trend?",
];

function StepChip({ step }: { step: Step }) {
  const isCached = !!step.cached;
  const tone =
    step.phase === "start"
      ? "bg-cream/50 ring-rule text-ink-2"
      : isCached
      ? "bg-amber/20 ring-amber/40 text-ink-2"
      : "bg-mint/40 ring-nomni/30 text-nomni-deep";

  const icon = step.phase === "start"
    ? <span className="size-1.5 rounded-full bg-nomni animate-pulse" />
    : <span className="text-nomni-leaf text-[11px] leading-none">✓</span>;

  const detail = step.phase === "start"
    ? step.label
    : `${step.label} · ${step.summary ?? "ok"}${typeof step.ms === "number" ? ` · ${step.ms}ms` : ""}${isCached ? ` (${step.cached})` : ""}`;

  return (
    <div className={`flex items-center gap-2 text-[11px] px-2.5 py-1 rounded-full ring-1 ${tone} w-fit max-w-full`}>
      {icon}
      <span className="font-mono text-[10px] text-ink-mute uppercase tracking-wider">{step.name}</span>
      <span className="truncate">{detail}</span>
    </div>
  );
}

export function ChatPanel({ outletId, market }: { outletId?: string; market?: string }) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  async function send(text: string) {
    if (!text.trim() || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "assistant", text: "", steps: [] }]);

    try {
      const r = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          messages: [...messages, { role: "user", text }],
          context: { outletId, market },
        }),
      });
      if (!r.ok || !r.body) throw new Error(`chat ${r.status}`);

      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let acc = "";
      let spec: unknown = undefined;
      const stepIndex = new Map<string, number>();
      let steps: Step[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf("\n")) >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === "text") {
              acc += String(ev.delta ?? "");
            } else if (ev.type === "spec") {
              spec = ev.spec;
            } else if (ev.type === "step") {
              const id = String(ev.id ?? "");
              const step: Step = {
                id,
                name: String(ev.name ?? "?"),
                label: String(ev.label ?? ev.name ?? "?"),
                phase: ev.phase === "done" ? "done" : "start",
                summary: typeof ev.summary === "string" ? ev.summary : undefined,
                cached: ev.cached ?? null,
                ms: typeof ev.ms === "number" ? ev.ms : undefined,
              };
              if (stepIndex.has(id)) {
                const i = stepIndex.get(id)!;
                steps = steps.map((s, k) => (k === i ? step : s));
              } else {
                stepIndex.set(id, steps.length);
                steps = [...steps, step];
              }
            } else if (ev.type === "error") {
              acc += `\n\n_⚠ ${ev.error ?? "agent error"}_`;
            } else if (ev.type === "tool") {
              // legacy event — ignored in favor of `step`
            }

            setMessages((m) => {
              const c = m.slice();
              c[c.length - 1] = { role: "assistant", text: acc, spec, steps };
              return c;
            });
          } catch {
            // partial line — ignore
          }
        }
      }
    } catch (e: unknown) {
      setMessages((m) => {
        const c = m.slice();
        c[c.length - 1] = {
          role: "assistant",
          text: `_couldn't reach the agent: ${e instanceof Error ? e.message : String(e)}_`,
          steps: [],
        };
        return c;
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <header className="px-4 py-3 border-b border-rule flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="size-6 rounded-md bg-nomni" />
          <div>
            <div className="text-sm font-medium text-ink">Nomni</div>
            <div className="text-[10px] uppercase tracking-wider text-ink-mute">
              meta-harness · {outletId ?? "no outlet"} {market ? `· ${market}` : ""}
            </div>
          </div>
        </div>
        <button
          onClick={() => setMessages([])}
          className="text-xs text-ink-mute hover:text-ink"
          aria-label="Clear chat"
        >
          new
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-ink-2">Ask anything about your procurement. The agent calls Zeemart's own endpoints and answers with live data — you'll see each step as it runs.</p>
            <div className="grid gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-left text-sm rounded-2xl bg-panel ring-1 ring-rule px-3 py-2 hover:ring-nomni"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
              {m.role === "user" ? (
                <div className="bg-mint/50 rounded-2xl rounded-br-sm px-3 py-2 text-sm max-w-[85%]">
                  {m.text}
                </div>
              ) : (
                <div className="space-y-2">
                  {m.steps.length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      {m.steps.map((s) => <StepChip key={s.id} step={s} />)}
                    </div>
                  ) : null}
                  {m.text ? (
                    <div className="text-sm text-ink-2 leading-relaxed prose prose-sm max-w-none nomni-markdown">
                      <Streamdown
                        parseIncompleteMarkdown
                        urlTransform={safeUrl}
                      >
                        {m.text}
                      </Streamdown>
                    </div>
                  ) : m.steps.length === 0 ? (
                    <div className="text-xs text-ink-mute italic">thinking…</div>
                  ) : null}
                  {m.spec ? (
                    <div className="rounded-[var(--radius-card)] bg-panel/80 ring-1 ring-rule p-2">
                      <Render spec={m.spec as never} />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="border-t border-rule p-3 bg-panel/40"
      >
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            rows={2}
            placeholder="Ask your procurement…"
            className="flex-1 resize-none rounded-2xl border border-rule bg-panel px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-nomni min-h-[44px] max-h-32"
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="rounded-full bg-nomni-deep text-white px-4 py-2 text-sm disabled:opacity-50"
          >
            {busy ? "…" : "send"}
          </button>
        </div>
        <div className="text-[10px] text-ink-mute mt-1.5">enter to send · shift+enter for newline</div>
      </form>
    </div>
  );
}
